import { useState } from 'react';
import { Heart, Calendar, MapPin, Users, Send, Eye, Copy, Share2, Edit, BarChart3, MessageSquare, Mail, MessageCircle } from 'lucide-react';
import { WeddingInvitation, Invitation, Guest } from '../types/invitation';
import { createLogger } from '../lib/logger';

const log = createLogger('invitation');

interface InvitationOverviewProps {
  weddingInvitation: WeddingInvitation;
  invitations: Invitation[];
  guests: Guest[];
  onEdit: () => void;
  onManageGuests: () => void;
  onViewInvitations: () => void;
}

export const InvitationOverview: React.FC<InvitationOverviewProps> = ({
  weddingInvitation,
  invitations,
  guests,
  onEdit,
  onViewInvitations
}) => {
  const [linkCopied, setLinkCopied] = useState(false);

  // 計算統計資料
  const relatedInvitations = invitations.filter(inv => inv.inviteCode === weddingInvitation.inviteCode);
  const stats = {
    total: relatedInvitations.length,
    sent: relatedInvitations.filter(inv => inv.status !== 'draft').length,
    viewed: relatedInvitations.filter(inv => inv.viewedAt).length,
    responded: relatedInvitations.filter(inv => inv.respondedAt).length,
    attending: relatedInvitations.filter(inv => inv.rsvpResponse?.attending === 'yes').length,
    declined: relatedInvitations.filter(inv => inv.rsvpResponse?.attending === 'no').length,
  };

  const inviteLink = `${window.location.origin}/invite/${weddingInvitation.inviteCode}`;

  const copyInviteLink = async () => {
    try {
      await navigator.clipboard.writeText(inviteLink);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    } catch (err) {
      log.error('複製失敗:', err);
    }
  };

  const shareViaLine = () => {
    window.open(`https://social-plugins.line.me/lineit/share?url=${encodeURIComponent(inviteLink)}`, '_blank');
  };

  const shareViaWhatsApp = () => {
    const message = `${weddingInvitation.brideName} & ${weddingInvitation.groomName} 的婚禮邀請函 💕\n\n我們誠摯邀請您參加我們的婚禮慶典！\n\n${inviteLink}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, '_blank');
  };

  const shareViaEmail = () => {
    const subject = `${weddingInvitation.brideName} & ${weddingInvitation.groomName} 的婚禮邀請`;
    const body = `親愛的朋友，\n\n我們誠摯邀請您參加我們的婚禮慶典！請點擊連結查看詳情並回覆出席狀況：\n\n${inviteLink}\n\n期待與您共度這個特別的日子！\n\n${weddingInvitation.brideName} & ${weddingInvitation.groomName}`;
    window.open(`mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`, '_blank');
  };

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-8">
      {/* 標題區域 */}
      <div className="text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-pink-100 rounded-full mb-4">
          <Heart className="h-8 w-8 text-pink-600" />
        </div>
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          邀請函概覽
        </h1>
        <p className="text-gray-600">
          管理您的婚禮邀請函並追蹤賓客回應
        </p>
      </div>

      {/* 邀請函資訊卡片 */}
      <div className="bg-gradient-to-br from-pink-50 to-purple-50 rounded-xl p-8 border border-pink-200">
        <div className="text-center mb-6">
          <Heart className="h-12 w-12 text-pink-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            {weddingInvitation.brideName} & {weddingInvitation.groomName}
          </h2>
          <p className="text-gray-600">的婚禮邀請函</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="flex items-center">
            <Calendar className="h-5 w-5 text-pink-500 mr-3" />
            <div>
              <p className="font-medium text-gray-900">{weddingInvitation.weddingDate}</p>
              <p className="text-sm text-gray-600">{weddingInvitation.weddingTime}</p>
            </div>
          </div>
          <div className="flex items-center">
            <MapPin className="h-5 w-5 text-pink-500 mr-3" />
            <div>
              <p className="font-medium text-gray-900">{weddingInvitation.venue}</p>
              <p className="text-sm text-gray-600">{weddingInvitation.address}</p>
            </div>
          </div>
        </div>
      </div>

      {/* 統計卡片 */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg border border-gray-200 p-6 text-center">
          <Users className="h-8 w-8 text-blue-500 mx-auto mb-2" />
          <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
          <p className="text-sm text-gray-600">總邀請數</p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-6 text-center">
          <Eye className="h-8 w-8 text-green-500 mx-auto mb-2" />
          <p className="text-2xl font-bold text-gray-900">{stats.viewed}</p>
          <p className="text-sm text-gray-600">已查看</p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-6 text-center">
          <Send className="h-8 w-8 text-purple-500 mx-auto mb-2" />
          <p className="text-2xl font-bold text-gray-900">{stats.responded}</p>
          <p className="text-sm text-gray-600">已回覆</p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-6 text-center">
          <Heart className="h-8 w-8 text-pink-500 mx-auto mb-2" />
          <p className="text-2xl font-bold text-gray-900">{stats.attending}</p>
          <p className="text-sm text-gray-600">確認出席</p>
        </div>
      </div>

      {/* 邀請連結分享 */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <Share2 className="h-5 w-5 text-pink-500 mr-2" />
          分享邀請連結
        </h3>
        
        <div className="space-y-4">
          <div className="flex items-center space-x-2">
            <input
              type="text"
              value={inviteLink}
              readOnly
              className="flex-1 border border-gray-300 rounded-md px-3 py-2 bg-gray-50 text-sm"
            />
            <button
              onClick={copyInviteLink}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                linkCopied 
                  ? 'bg-green-500 text-white' 
                  : 'bg-gray-500 hover:bg-gray-600 text-white'
              }`}
            >
              {linkCopied ? '已複製!' : <Copy className="h-4 w-4" />}
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <button
              onClick={shareViaLine}
              className="bg-[#06C755] hover:bg-[#05b34c] text-white px-4 py-3 rounded-lg flex items-center justify-center transition-colors"
            >
              <MessageCircle className="h-4 w-4 mr-2" />
              LINE
            </button>
            <button
              onClick={shareViaWhatsApp}
              className="bg-green-500 hover:bg-green-600 text-white px-4 py-3 rounded-lg flex items-center justify-center transition-colors"
            >
              <MessageSquare className="h-4 w-4 mr-2" />
              WhatsApp
            </button>
            <button
              onClick={shareViaEmail}
              className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-3 rounded-lg flex items-center justify-center transition-colors"
            >
              <Mail className="h-4 w-4 mr-2" />
              郵件
            </button>
          </div>
        </div>
      </div>

      {/* 快速操作 */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <button
          onClick={() => window.open(`/invite/${weddingInvitation.inviteCode}`, '_blank')}
          className="bg-pink-500 hover:bg-pink-600 text-white px-6 py-4 rounded-lg flex items-center justify-center transition-colors"
        >
          <Eye className="h-5 w-5 mr-2" />
          預覽邀請函
        </button>
        <button
          onClick={onEdit}
          className="border border-gray-300 text-gray-700 hover:bg-gray-50 px-6 py-4 rounded-lg flex items-center justify-center transition-colors"
        >
          <Edit className="h-5 w-5 mr-2" />
          編輯邀請函
        </button>
        <button
          onClick={onViewInvitations}
          className="bg-purple-500 hover:bg-purple-600 text-white px-6 py-4 rounded-lg flex items-center justify-center transition-colors"
        >
          <BarChart3 className="h-5 w-5 mr-2" />
          查看詳細統計
        </button>
      </div>

      {/* 最近回覆 */}
      {stats.responded > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Users className="h-5 w-5 text-pink-500 mr-2" />
            最近回覆 ({stats.responded})
          </h3>
          
          <div className="space-y-3">
            {relatedInvitations
              .filter(inv => inv.respondedAt)
              .slice(0, 5)
              .map((invitation) => {
                const guest = guests.find(g => g.id === invitation.guestId);
                return (
                  <div key={invitation.id} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-b-0">
                    <div className="flex items-center">
                      <div className={`w-3 h-3 rounded-full mr-3 ${
                        invitation.rsvpResponse?.attending === 'yes' ? 'bg-green-500' :
                        invitation.rsvpResponse?.attending === 'no' ? 'bg-red-500' : 'bg-yellow-500'
                      }`} />
                      <div>
                        <p className="font-medium text-gray-900">{guest?.name || '未知賓客'}</p>
                        <p className="text-sm text-gray-600">{guest?.email}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium">
                        {invitation.rsvpResponse?.attending === 'yes' ? '確認出席' :
                         invitation.rsvpResponse?.attending === 'no' ? '無法出席' : '待確認'}
                      </p>
                      <p className="text-xs text-gray-500">
                        {invitation.respondedAt?.toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                );
              })}
          </div>
          
          {stats.responded > 5 && (
            <button
              onClick={onViewInvitations}
              className="w-full mt-4 text-center text-pink-600 hover:text-pink-700 font-medium"
            >
              查看全部 {stats.responded} 個回覆 →
            </button>
          )}
        </div>
      )}
    </div>
  );
};